import React, { useEffect, useMemo } from "react"
import { useFormik } from "formik"
import { Row, Col, Form, Label } from "reactstrap"
import DatePicker from "react-datepicker"
import { TextInput } from "components/TextInput"
import { Button } from "components/Button"
import {
   initialValues,
   FIELDS_NAME,
   validationSchema,
   MIN_TOTAL_SLOTS,
   MAX_TOTAL_SLOTS,
   NUMBER_REGEX,
} from "./FormConfig"
const TOTAL_SLOTS = 4

const Overrides = ({
   override = {},
   actionName = "ADD",
   isLoading = false,
   onSubmit,
}) => {
   const today = useMemo(() => new Date(), [])
   const {
      dateFrom: initStart = null,
      dateTo: initEnd = null,
      noOfSlots: initTotal = 0,
      premiumSlots: initPrem = 0,
      regularSlots: initReg = 0,
   } = override
   useEffect(() => {
      formik.setValues({
         [FIELDS_NAME.START_DATE]: initStart ? new Date(initStart) : today,
         [FIELDS_NAME.END_DATE]: initEnd ? new Date(initEnd) : today,
         [FIELDS_NAME.TOTAL_SLOTS]: initTotal,
         [FIELDS_NAME.PREMIUM_SLOTS]: initPrem,
         [FIELDS_NAME.REGULAR_SLOTS]: initReg,
      })
   }, [override])

   const formik = useFormik({
      enableReinitialize: true,
      initialValues: initialValues,
      validationSchema: validationSchema,
      validate: vals => {
         const errors = {}

         const premium = Number(vals.premiumSlots)
         const regular = Number(vals.regularSlots)
         const total = premium + regular || Number(vals.totalSlots)

         if (total < MIN_TOTAL_SLOTS) {
            errors.totalSlots = `Total Slots can not be lower than ${MIN_TOTAL_SLOTS}`
         } else if (total > MAX_TOTAL_SLOTS) {
            errors.totalSlots = `Total Slots can not be greater than ${MAX_TOTAL_SLOTS}`
         }

         return errors
      },
      onSubmit: values => onSubmit(values),
   })

   const {
      values,
      errors,
      touched,
      setFieldValue,
      handleChange,
      handleSubmit,
   } = formik

   return (
      <Form onSubmit={handleSubmit} className="py-2 mx-2 overrides">
         {/* Row 1: From / To */}
         <Row className="pt-2 mb-3 align-items-center">
            <Col md={2}>
               <Label className="me-2 mb-0" for={FIELDS_NAME.START_DATE}>
                  From:
               </Label>
            </Col>
            <Col md={4} className="date-picker-col">
               <DatePicker
                  required
                  id={FIELDS_NAME.START_DATE}
                  showIcon
                  selected={values[FIELDS_NAME.START_DATE]}
                  onChange={d => setFieldValue(FIELDS_NAME.START_DATE, d)}
                  dateFormat="MM/dd/yyyy"
                  className={`override-datepicker form-control ${
                     errors[FIELDS_NAME.START_DATE] &&
                     touched[FIELDS_NAME.START_DATE]
                        ? "is-invalid"
                        : ""
                  }`}
               />
               {errors[FIELDS_NAME.START_DATE] &&
                  touched[FIELDS_NAME.START_DATE] && (
                     <div className="invalid-feedback">
                        {errors[FIELDS_NAME.START_DATE]}
                     </div>
                  )}
            </Col>

            <Col md={2}>
               <Label className="me-2 mb-0" for={FIELDS_NAME.END_DATE}>
                  To:
               </Label>
            </Col>
            <Col md={4} className="date-picker-col">
               <DatePicker
                  required
                  id={FIELDS_NAME.END_DATE}
                  showIcon
                  selected={values[FIELDS_NAME.END_DATE]}
                  onChange={d => setFieldValue([FIELDS_NAME.END_DATE], d)}
                  dateFormat="MM/dd/yyyy"
                  className={`override-datepicker form-control ${
                     errors[FIELDS_NAME.END_DATE] &&
                     touched[FIELDS_NAME.END_DATE]
                        ? "is-invalid"
                        : ""
                  }`}
               />
               {errors[FIELDS_NAME.END_DATE] &&
                  touched[FIELDS_NAME.END_DATE] && (
                     <div className="invalid-feedback">
                        {errors[FIELDS_NAME.END_DATE]}
                     </div>
                  )}
            </Col>
         </Row>

         {/* Row 2: Total Slots */}
         <Row className="pt-2 mb-3 align-items-center ">
            <Col md={2}>
               <Label className="me-2 mb-0" for={FIELDS_NAME.TOTAL_SLOTS}>
                  Total Slots:
               </Label>
            </Col>
            <Col md={10}>
               <TextInput
                  id={FIELDS_NAME.TOTAL_SLOTS}
                  name={FIELDS_NAME.TOTAL_SLOTS}
                  readOnly={true}
                  placeholder="0"
                  onChange={handleChange}
                  value={
                     Number(values[FIELDS_NAME.REGULAR_SLOTS]) +
                     Number(values[FIELDS_NAME.PREMIUM_SLOTS])
                  }
                  className={`flex-1 text-center read-only ${
                     errors[FIELDS_NAME.TOTAL_SLOTS] &&
                     touched[FIELDS_NAME.TOTAL_SLOTS]
                        ? "is-invalid"
                        : ""
                  }`}
               />
               {errors[FIELDS_NAME.TOTAL_SLOTS] && (
                  <div className="invalid-feedback">
                     {errors[FIELDS_NAME.TOTAL_SLOTS]}
                  </div>
               )}
            </Col>
         </Row>

         {/* Row 3: Premium & Regular */}
         <Row className="pt-2 mb-4 align-items-start">
            <Col md={2}>
               <Label className="mt-1 mb-0" for={FIELDS_NAME.PREMIUM_SLOTS}>
                  Premium:
               </Label>
            </Col>
            <Col md={4}>
               <TextInput
                  id={FIELDS_NAME.PREMIUM_SLOTS}
                  name={FIELDS_NAME.PREMIUM_SLOTS}
                  value={values[FIELDS_NAME.PREMIUM_SLOTS]}
                  onChange={e => {
                     if (!e.target.value.match(NUMBER_REGEX)) {
                        return
                     }
                     handleChange(e)
                  }}
                  className={`text-center ${
                     errors[FIELDS_NAME.PREMIUM_SLOTS] &&
                     touched[FIELDS_NAME.PREMIUM_SLOTS]
                        ? "is-invalid"
                        : ""
                  }`}
                  placeholder="Enter Premium Slot"
               />
               {errors[FIELDS_NAME.PREMIUM_SLOTS] &&
                  touched[FIELDS_NAME.PREMIUM_SLOTS] && (
                     <div className="invalid-feedback">
                        {errors[FIELDS_NAME.PREMIUM_SLOTS]}
                     </div>
                  )}
            </Col>

            <Col md={2}>
               <Label className="mt-1 mb-0" for={FIELDS_NAME.REGULAR_SLOTS}>
                  Regular:
               </Label>
            </Col>
            <Col md={4}>
               <TextInput
                  id={FIELDS_NAME.REGULAR_SLOTS}
                  name={FIELDS_NAME.REGULAR_SLOTS}
                  type="number"
                  value={values[FIELDS_NAME.REGULAR_SLOTS]}
                  onChange={e => {
                     if (!e.target.value.match(NUMBER_REGEX)) {
                        return
                     }
                     handleChange(e)
                  }}
                  className={`text-center ${
                     errors[FIELDS_NAME.REGULAR_SLOTS] &&
                     touched[FIELDS_NAME.REGULAR_SLOTS]
                        ? "is-invalid"
                        : ""
                  }`}
                  placeholder="Enter Regular Slot"
               />
               {errors[FIELDS_NAME.REGULAR_SLOTS] && (
                  <div className="invalid-feedback">
                     {errors[FIELDS_NAME.REGULAR_SLOTS]}
                  </div>
               )}
            </Col>
         </Row>

         <div className="d-flex justify-content-center">
            <Button
               className="header-button headerButtonResponsive"
               type="submit"
               title={actionName}
               isLoading={isLoading}
            />
         </div>
      </Form>
   )
}

export default Overrides
